import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const onboardingTips = [
  {
    icon: 'Layers',
    title: 'Create a Stack',
    desc: 'Name your purchase decision — a sofa, a car, a vacation. Give it context so Stackd knows what to compare.',
    step: '01',
  },
  {
    icon: 'ClipboardList',
    title: 'Define Your Needs',
    desc: 'Set your budget, timeline, and must-haves. Our AI helps you turn vague ideas into clear evaluation criteria.',
    step: '02',
  },
  {
    icon: 'PlusCircle',
    title: 'Add Options',
    desc: 'Search online vendors or paste links. Stackd pulls pricing, specs, and details into one place automatically.',
    step: '03',
  },
  {
    icon: 'BarChart3',
    title: 'Compare & Decide',
    desc: 'See a weighted score for each option based on what matters to you. Pick with confidence, not guesswork.',
    step: '04',
  },
];

const sampleOptions = [
  { name: 'Article Sven Sofa', price: '$1,299', score: 91, color: '#F58A54', tags: ['Free delivery', 'In stock'] },
  { name: 'West Elm Haven', price: '$1,850', score: 74, color: '#F06F6B', tags: ['White glove', '8 wk lead'] },
  { name: 'IKEA KIVIK', price: '$649', score: 68, color: '#E0528A', tags: ['Self-assemble', 'In stock'] },
];

const SampleStackCard = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
      {/* Card header accent */}
      <div className="h-1.5 w-full" style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }} />
      <div className="p-5">
        {/* Stack meta */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="Sofa" size={18} color="white" />
            </div>
            <div>
              <div className="font-bold text-foreground text-sm">Living Room Sofa</div>
              <div className="text-xs text-text-secondary">Home Goods · 3 options</div>
            </div>
          </div>
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(240,111,107,0.12)', color: '#F06F6B' }}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-current" />
            Active
          </div>
        </div>

        {/* Options comparison */}
        <div className="space-y-2.5 mb-4">
          {sampleOptions?.map((opt, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-foreground truncate">{opt?.name}</span>
                  <span className="text-xs font-bold ml-2 flex-shrink-0" style={{ color: opt?.color }}>{opt?.price}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-1.5 rounded-full transition-all duration-700"
                      style={{ width: `${opt?.score}%`, background: `linear-gradient(to right, ${opt?.color}, ${opt?.color}cc)` }}
                    />
                  </div>
                  <span className="text-xs font-bold w-7 text-right" style={{ color: opt?.color }}>{opt?.score}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Winner callout */}
        <div
          className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl mb-4"
          style={{ background: 'rgba(245,138,84,0.08)', border: '1px solid rgba(245,138,84,0.2)' }}
        >
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
          >
            <Icon name="Trophy" size={13} color="white" />
          </div>
          <div>
            <div className="text-xs font-bold text-foreground">Top Pick: Article Sven Sofa</div>
            <div className="text-xs text-text-secondary">Score 91 · Best value for your needs</div>
          </div>
        </div>

        {/* Expandable detail */}
        {expanded && (
          <div className="mb-4 space-y-2">
            <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Why it wins</div>
            {[
              { label: 'Price vs. budget', val: '87%', icon: 'DollarSign' },
              { label: 'Delivery speed', val: '95%', icon: 'Truck' },
              { label: 'Style match', val: '90%', icon: 'Sparkles' },
            ]?.map((row, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 bg-muted rounded-xl">
                <div className="flex items-center gap-2 text-xs text-text-secondary">
                  <Icon name={row?.icon} size={12} />
                  {row?.label}
                </div>
                <span className="text-xs font-bold" style={{ color: '#F58A54' }}>{row?.val}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all duration-200"
        >
          <Icon name={expanded ? 'ChevronUp' : 'Eye'} size={13} />
          {expanded ? 'Hide Details' : 'See How It Works'}
        </button>
      </div>
    </div>
  );
};

const EmptyState = ({ hasFilters, onClearFilters }) => {
  const navigate = useNavigate();

  if (hasFilters) {
    return (
      <div className="text-center py-20">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-md"
          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
        >
          <Icon name="Search" size={28} color="white" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">No stacks found</h3>
        <p className="text-text-secondary mb-6 max-w-md mx-auto text-sm leading-relaxed">
          No stacks match your current filters. Try adjusting your search or clear all filters.
        </p>
        <button
          onClick={onClearFilters}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-border text-sm font-semibold text-foreground hover:border-primary/40 hover:bg-primary/5 transition-all duration-200"
        >
          <Icon name="X" size={14} />
          Clear All Filters
        </button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* ── Hero welcome ── */}
      <div
        className="relative overflow-hidden rounded-2xl mb-8 px-8 py-10 text-center"
        style={{ background: 'linear-gradient(135deg, #F58A54 0%, #F06F6B 50%, #E0528A 100%)' }}
      >
        {/* Decorative blobs */}
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute top-4 left-8 opacity-20">
          <div className="w-10 h-10 rounded-xl bg-white/30 border border-white/40 flex items-center justify-center rotate-12">
            <Icon name="Layers" size={16} color="white" />
          </div>
        </div>
        <div className="absolute bottom-4 right-12 opacity-20">
          <div className="w-8 h-8 rounded-xl bg-white/30 border border-white/40 flex items-center justify-center -rotate-6">
            <Icon name="BarChart3" size={14} color="white" />
          </div>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/20 border border-white/30 text-white text-xs font-semibold mb-4">
            <Icon name="Sparkles" size={12} color="white" />
            Welcome to Stackd
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
            Make every big purchase<br />with total clarity
          </h2>
          <p className="text-white/80 text-base max-w-lg mx-auto mb-6 leading-relaxed">
            Stackd helps you compare vendors, weigh what matters, and pick the right option — not just the cheapest one.
          </p>
          <button
            onClick={() => navigate('/create-project-flow')}
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-white rounded-xl font-bold text-sm shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            style={{ color: '#F06F6B' }}
          >
            <Icon name="Plus" size={16} />
            Create Your First Stack
          </button>
        </div>
      </div>
      {/* ── Two-column: tips + sample stack ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">

        {/* Onboarding tips — wider column */}
        <div className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              <Icon name="Zap" size={13} color="white" />
            </div>
            <h3 className="text-base font-bold text-foreground">How Stackd works</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {onboardingTips?.map((tip, i) => (
              <div
                key={i}
                className="relative bg-card border border-border rounded-2xl p-4 hover:border-primary/30 hover:shadow-sm transition-all duration-200 overflow-hidden group"
              >
                {/* Step number watermark */}
                <div
                  className="absolute -top-2 -right-1 text-5xl font-black opacity-[0.04] select-none pointer-events-none leading-none"
                  style={{ color: '#F06F6B' }}
                >
                  {tip?.step}
                </div>
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-200"
                    style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                  >
                    <Icon name={tip?.icon} size={16} color="white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-foreground mb-1">{tip?.title}</div>
                    <div className="text-xs text-text-secondary leading-relaxed">{tip?.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sample stack walkthrough — narrower column */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F06F6B, #E0528A)' }}
            >
              <Icon name="Eye" size={13} color="white" />
            </div>
            <h3 className="text-base font-bold text-foreground">Sample stack</h3>
          </div>
          <SampleStackCard />
        </div>
      </div>
      {/* ── Bottom CTA strip ── */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-5 bg-card border border-border rounded-2xl">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
          >
            <Icon name="Rocket" size={18} color="white" />
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">Ready to start comparing?</div>
            <div className="text-xs text-text-secondary">Takes less than 2 minutes to set up your first stack.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => navigate('/how-it-works')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <Icon name="HelpCircle" size={14} />
            How It Works
          </button>
          <button
            onClick={() => navigate('/create-project-flow')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
            style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B)' }}
          >
            <Icon name="Plus" size={14} color="white" />
            Create Stack
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmptyState;